import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"

export interface SortElement {
	sorted: boolean
	unsorted: boolean
	empty: boolean
}

export interface CampaignsState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number
	id: number
	idLogo: string | undefined
	campaignName: string | undefined
	campaignLogo: string | undefined
	enabled: boolean
	deleted: boolean
	authorizationType: string | undefined
}

export interface BankAccountState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number
	id: number
	contoCorrente: string | undefined
	senderAccount: string | undefined
	note: string | undefined
	enabled: boolean
	deleted: boolean
}

export interface CorporatesState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number | undefined
	id: number | undefined
	corporateSenderId: string | undefined
	enabled: boolean | undefined
	corporateSenderName: string | undefined
	bank: string | undefined
	campaigns: CampaignsState[]
	contiCorrenti: BankAccountState[]
	logo: string | undefined
	primaryColor: string | undefined
	secondaryColor: string | undefined
	deleted: boolean | undefined
	autoAccept: boolean | undefined
	timeToExpiry: number | undefined
	hasBankAuthorizer: boolean | undefined
	whatsapp: boolean | undefined
	daysToFirstReminder: number | undefined
	daysToSecondReminder: number | undefined
	checkIban: boolean | undefined
	plickOpen: boolean | undefined
	voucher: boolean | undefined
	fileVersion: number | undefined
}

export interface PageableElement {
	content: CorporatesState[]
	pageable: {
		sort: SortElement
		offset: number
		pageNumber: number
		pageSize: number
		paged: boolean
		unpaged: boolean
	}
}

export interface PageElement {
	content: CorporatesState[]
	pageable: PageableElement
	totalElements: number
	totalPages: number
	last: boolean
	size: number
	number: number
	first: boolean
	numberOfElements: number
	empty: boolean
}

export interface CorporatesListResponse {
	nextUrl: null | string
	page: PageElement
}

export interface CorporatesListState {
	loading: boolean
	error: undefined | AxiosError<string>
	loadingFetch: boolean
	errorFetch: undefined | AxiosError<string>
	list: CorporatesListResponse | []
}

export interface AllCorporatesListState {
	loading: boolean
	error: undefined | AxiosError<string>
	list: CorporateEssential[]
	selected: CorporateEssential | undefined
}

export interface CorporatesPayload {
	filter?: any | undefined
	sort?: any | undefined
	page?: number | undefined
	pageSize?: number | undefined
}

export interface FetchCorporatesPayload {
	apiName: string
	url: string
}

export interface CorporatesEssentialPayload {
	filter?: any | undefined
	sort?: any | undefined
}

export interface CorporateEssential {
	id: string
	name: string
	img: undefined | string
	primaryColor: undefined | string
	secondaryColor: undefined | string
	isPlickOpen: boolean
	enabled: boolean
	deleted: boolean
	idBank: string
	campaigns: CampaignsState[]
	contiCorrenti: BankAccountState[]
}

export interface IPatchResponse {}

interface IPatchPayload {
	idBank: string | undefined
	contoId: number
	bankAccountData: BankAccountState
}

export const updateBankAccount = createAsyncThunk<
	IPatchResponse,
	IPatchPayload,
	{ rejectValue: AxiosError }
>(
	"bankAccount/activation/patch",
	async (
		{ idBank, contoId, bankAccountData }: IPatchPayload,
		{ rejectWithValue }
	) => {
		try {
			const headerConfig: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}
			const response: AxiosResponse = await axios.patch(
				`${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicontoCorrente/v2/${idBank}/update/${contoId}`,
				bankAccountData,
				headerConfig
			)
			return response
		} catch (error: AxiosError) {
			return rejectWithValue(error?.response)
		}
	}
)
